import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The Environment each scene shows, and the one a viewport pass or a probe capture draws it with.
 * Each part of an Environment, such as its background or its fog, writes the scene only while its
 * Environment is on the scene, so a pass through a camera with an Environment of its own draws that
 * one over a world whose scene shows another, as Godot forces it per render
 * (`renderer_scene_cull.cpp:3594-3597`).
 */
import { createContext, useContext, useLayoutEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { perObject } from '../../utils/perObject';
import { drawAtExposure, worldExposureOfScene } from '../exposure/sceneExposure';
const environmentsOf = perObject((_scene) => ({
    shown: null,
    own: null,
    ownChanges: 0,
    applied: null,
    parts: new Map(),
    onScene: [],
}));
/** Counts a change of `environment`, which a probe sees only for the world's own. */
function noteChangeOf(environments, environment) {
    if (environment !== null && environment === environments.own)
        environments.ownChanges += 1;
}
/** Takes the parts on the scene off, last first, then puts the parts of `environment` on. */
function putOn(environments, environment) {
    const { onScene } = environments;
    for (let index = onScene.length - 1; index >= 0; index -= 1)
        onScene[index].takeOff();
    onScene.length = 0;
    environments.applied = environment;
    if (environment === null)
        return;
    for (const part of environments.parts.get(environment) ?? [])
        onScene.push({ part, takeOff: part() });
}
/**
 * Has `part` of `environment` write `scene` whenever that Environment is on it, until the returned
 * function runs.
 *
 * @canonical A part of an Environment on its scene
 */
export function registerEnvironmentPart(scene, environment, part) {
    const environments = environmentsOf(scene);
    const parts = environments.parts.get(environment) ?? new Set();
    environments.parts.set(environment, parts);
    parts.add(part);
    noteChangeOf(environments, environment);
    if (environments.applied === environment)
        environments.onScene.push({ part, takeOff: part() });
    return () => {
        parts.delete(part);
        if (parts.size === 0)
            environments.parts.delete(environment);
        noteChangeOf(environments, environment);
        const index = environments.onScene.findIndex((entry) => entry.part === part);
        if (index === -1)
            return;
        environments.onScene[index].takeOff();
        environments.onScene.splice(index, 1);
    };
}
/**
 * Counts a change of what a part of `environment` draws on `scene` that no registration shows, such
 * as a sky bake that lands a frame after the render that asked for it.
 *
 * @canonical A part of an Environment on its scene
 */
export function noteEnvironmentChange(scene, environment) {
    noteChangeOf(environmentsOf(scene), environment);
}
/**
 * Shows `environment` on `scene` outside a pass, until the returned function runs. One world
 * shows one Environment on its scene.
 */
function showEnvironment(scene, environment) {
    const environments = environmentsOf(scene);
    environments.shown = environment;
    putOn(environments, environment);
    return () => {
        if (environments.shown !== environment)
            return;
        environments.shown = null;
        putOn(environments, null);
    };
}
/** Runs `draw` with `environment` on `scene`, and puts the shown one back after it. */
function drawWithEnvironment(scene, environment, draw) {
    const environments = environmentsOf(scene);
    if (environment === environments.applied)
        return draw();
    try {
        putOn(environments, environment);
        return draw();
    }
    finally {
        putOn(environments, environments.shown);
    }
}
/**
 * Runs `draw` with `scene` set for `environment` at `exposure`, and puts the scene back after it. A
 * draw with what the scene holds changes nothing. The Environment goes on first, since its parts
 * follow the exposure of each render of the draw (`followSceneExposure`).
 *
 * @canonical The Environment and the exposure a draw of a scene takes
 */
export function drawWith(scene, environment, exposure, draw) {
    return drawWithEnvironment(scene, environment, () => drawAtExposure(scene, exposure, draw));
}
/**
 * Runs `draw` with the world's own Environment and exposure on `scene`, as Godot captures a
 * reflection probe with the scenario's and not the camera's (`renderer_scene_cull.cpp:3692`).
 *
 * @canonical The Environment and the exposure a draw of a scene takes
 */
export function drawAsWorld(scene, draw) {
    return drawWith(scene, environmentsOf(scene).own, worldExposureOfScene(scene), draw);
}
/**
 * A count that grows each time the world's own Environment on `scene`, or one of its parts,
 * changes, so a probe captures again only for a change it would capture.
 *
 * @canonical The Environment and the exposure a draw of a scene takes
 */
export function ownEnvironmentChanges(scene) {
    return environmentsOf(scene).ownChanges;
}
/**
 * Names `environment` the own Environment of the world whose scene the caller draws in, null for
 * none. One world names one on its scene.
 *
 * @canonical The Environment and the exposure a draw of a scene takes
 */
export function useOwnEnvironment(environment) {
    const scene = useThree((state) => state.scene);
    useLayoutEffect(() => {
        const environments = environmentsOf(scene);
        environments.own = environment;
        noteChangeOf(environments, environment);
        return () => {
            if (environments.own !== environment)
                return;
            noteChangeOf(environments, environment);
            environments.own = null;
        };
    }, [scene, environment]);
}
/** The Environment the parts below belong to, null outside one. */
const EnvironmentKeyContext = createContext(null);
/**
 * The Environment the calling part belongs to.
 *
 * @canonical A part of an Environment on its scene
 */
export function useEnvironmentKey() {
    const environment = useContext(EnvironmentKeyContext);
    if (!environment)
        throw new Error('expected an Environment around an Environment part, found none');
    return environment;
}
/**
 * Mounts the parts of `environment` below, which its scene shows outside a pass.
 *
 * @canonical A part of an Environment on its scene
 */
export function ShownEnvironment({ environment, children, }) {
    const scene = useThree((state) => state.scene);
    // A layout effect, so the scene shows the Environment before a part's effect registers.
    useLayoutEffect(() => showEnvironment(scene, environment), [scene, environment]);
    return _jsx(DrawnEnvironment, { environment: environment, children: children });
}
/**
 * Mounts the parts of `environment` below, which its scene shows only while a pass draws with it.
 *
 * @canonical A part of an Environment on its scene
 */
export function DrawnEnvironment({ environment, children, }) {
    return _jsx(EnvironmentKeyContext.Provider, { value: environment, children: children });
}
/**
 * Draws `texture` at `intensity` behind `scene`, and answers the restore of what it found.
 *
 * @canonical A texture shown as the background of a scene
 */
export function showBackground(scene, texture, intensity) {
    const previousBackground = scene.background;
    const previousIntensity = scene.backgroundIntensity;
    scene.background = texture;
    scene.backgroundIntensity = intensity;
    return () => {
        scene.background = previousBackground;
        scene.backgroundIntensity = previousIntensity;
    };
}
//# sourceMappingURL=sceneEnvironment.js.map